'use client'

/**
 * CrewMemberChrome
 *
 * Mature Crew chrome — one component, two hosts:
 * - panel (Your crew): draft Mate face + idle status (NOT_BOUND_YET)
 * - header (chat-ui): multi-mate roster strip for bound Crew Chat
 *
 * Do not fork a second presence component. Chat header uses this.
 * Preserve LosiAnimated (LosiAvatar). No Muse/Vesper.
 */

import { useEffect, useState } from 'react'

import { LosiAvatar } from '@/components/losi-bot/losi-avatar'
import {
  CREW_SURFACE_PANEL_CLASSNAME,
  CREW_SURFACE_PANEL_STACK_CLASSNAME,
  CREW_FEED_MICRO_BADGE_CLASSNAME,
  CREW_MEET_YOUR_CREW_HONESTY_NOTE_CLASSNAME,
  CREW_FEED_STATUS_MUTED_CLASSNAME,
  CREW_FEED_SERIF_SECTION_HEADING_CLASSNAME,
  CREW_FEED_CHIP_WRAP_CLASSNAME,
  CREW_FEED_CHIP_WRAP_DENSE_CLASSNAME,
  CREW_FEED_AVATAR_ROW_CLASSNAME,
  CREW_FEED_AVATAR_DECORATIVE_SHELL_CLASSNAME,
  CREW_MEMBER_CHROME_COPY_COLUMN_CLASSNAME,
  CREW_MEMBER_DISPLAY_NAME_CLASSNAME
} from '@/lib/crew/feed-card'
import { getCrewMemberDraft } from '@/lib/crew/feed-api'
import { CREW_MEMBER_BIND_STATUS_LABEL } from '@/lib/crew/member-bind'
import {
  CREW_MEMBER_CHROME_FACE_FALLBACK_LABEL,
  CREW_MEMBER_CHROME_HONESTY,
  CREW_MEMBER_CHROME_REGION_A11Y,
  CREW_MEMBER_CHROME_STATUS_IDLE,
  CREW_MEMBER_CHROME_STATUS_LABEL,
  CREW_MEMBER_CHROME_TITLE,
  resolveCrewMemberChrome,
  resolveCrewMemberChromeAvatarState,
  resolveCrewMemberChromeHeaderSeatStatus,
  type CrewMemberChromeActivity,
  type CrewMemberChromeModel
} from '@/lib/crew/member-chrome'
import {
  type CrewChatMateSeat,
  type CrewChatRoster
} from '@/lib/crew/crew-chat'
import { mateIdToFeedShape } from '@/lib/crew/mates'
import { cn } from '@/lib/utils'

export type CrewMemberChromeProps = {
  className?: string
  /**
   * panel = Your crew draft chrome (default).
   * header = compact roster strip for chat-ui (requires roster).
   */
  variant?: 'panel' | 'header'
  /**
   * Optional pre-resolved model (tests / hosts that already loaded draft).
   * When omitted in panel mode, loads via getCrewMemberDraft.
   */
  model?: CrewMemberChromeModel | null
  /** Bound Crew Chat roster — required for header variant. */
  roster?: CrewChatRoster | null
  /** Active speaker for the current turn. */
  activeMateId?: string | null
  /** Per-mate activity (thinking / working / …). Missing → idle. */
  activityByMateId?: Partial<Record<string, CrewMemberChromeActivity>>
}

function HeaderSeat({
  seat,
  activeMateId,
  activityByMateId
}: {
  seat: CrewChatMateSeat
  activeMateId?: string | null
  activityByMateId?: CrewMemberChromeProps['activityByMateId']
}) {
  const status = resolveCrewMemberChromeHeaderSeatStatus({
    mateId: seat.mateId,
    activeMateId,
    activityByMateId
  })
  const shape = mateIdToFeedShape(seat.mateId) ?? 'peta'
  const statusLabel =
    status === CREW_MEMBER_CHROME_STATUS_IDLE ? '' : status

  return (
    <div
      className={cn(
        CREW_FEED_AVATAR_ROW_CLASSNAME,
        'items-center gap-1.5 rounded-full border border-border/40 bg-background/60 px-2 py-1',
        seat.mateId === activeMateId && 'border-foreground/30'
      )}
      title={
        statusLabel
          ? `${seat.displayName} · ${statusLabel}`
          : seat.displayName
      }
    >
      <div className={CREW_FEED_AVATAR_DECORATIVE_SHELL_CLASSNAME} aria-hidden>
        <LosiAvatar
          color="blanc"
          fitBody
          shape={shape}
          size={24}
          state={resolveCrewMemberChromeAvatarState(status)}
        />
      </div>
      <span className="text-xs font-medium text-foreground">
        {seat.displayName}
      </span>
      {seat.role === 'leader' ? (
        <span className={CREW_FEED_STATUS_MUTED_CLASSNAME}>lead</span>
      ) : null}
      {statusLabel ? (
        <span className={CREW_FEED_STATUS_MUTED_CLASSNAME}>{statusLabel}</span>
      ) : null}
    </div>
  )
}

function CrewMemberChromeHeader({
  roster,
  activeMateId,
  activityByMateId,
  className
}: {
  roster: CrewChatRoster
  activeMateId?: string | null
  activityByMateId?: CrewMemberChromeProps['activityByMateId']
  className?: string
}) {
  return (
    <div
      aria-label={CREW_MEMBER_CHROME_REGION_A11Y.trim() || 'Crew'}
      className={cn(CREW_FEED_CHIP_WRAP_DENSE_CLASSNAME, 'px-1 py-1', className)}
      role="group"
    >
      {roster.seats.map(seat => (
        <HeaderSeat
          activeMateId={activeMateId}
          activityByMateId={activityByMateId}
          key={seat.mateId}
          seat={seat}
        />
      ))}
    </div>
  )
}

function CrewMemberChromePanel({
  className,
  model
}: {
  className?: string
  model?: CrewMemberChromeModel | null
}) {
  const [resolved, setResolved] = useState<CrewMemberChromeModel | null>(
    model ?? null
  )
  const [isLoading, setIsLoading] = useState(model === undefined)

  useEffect(() => {
    if (model !== undefined) {
      setResolved(model)
      setIsLoading(false)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const draft = await getCrewMemberDraft()
        if (cancelled) return
        setResolved(resolveCrewMemberChrome(draft))
      } catch {
        if (!cancelled) setResolved(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [model])

  if (isLoading || !resolved) return null

  return (
    <section
      aria-labelledby={
        CREW_MEMBER_CHROME_TITLE ? 'crew-member-chrome-title' : undefined
      }
      aria-label={
        CREW_MEMBER_CHROME_TITLE
          ? undefined
          : CREW_MEMBER_CHROME_REGION_A11Y.trim() || undefined
      }
      className={cn(
        CREW_SURFACE_PANEL_CLASSNAME,
        CREW_SURFACE_PANEL_STACK_CLASSNAME,
        className
      )}
    >
      <div className={CREW_FEED_CHIP_WRAP_CLASSNAME}>
        {CREW_MEMBER_CHROME_TITLE ? (
          <h2
            className={CREW_FEED_SERIF_SECTION_HEADING_CLASSNAME}
            id="crew-member-chrome-title"
          >
            {CREW_MEMBER_CHROME_TITLE}
          </h2>
        ) : null}
        {CREW_MEMBER_BIND_STATUS_LABEL ? (
          <span
            className={CREW_FEED_MICRO_BADGE_CLASSNAME}
            title={CREW_MEMBER_CHROME_HONESTY || undefined}
          >
            {CREW_MEMBER_BIND_STATUS_LABEL}
          </span>
        ) : null}
        {CREW_MEMBER_CHROME_STATUS_LABEL ? (
          <span
            className={CREW_FEED_MICRO_BADGE_CLASSNAME}
            data-crew-status={CREW_MEMBER_CHROME_STATUS_IDLE}
          >
            {CREW_MEMBER_CHROME_STATUS_LABEL}
          </span>
        ) : null}
      </div>

      <div className={CREW_FEED_AVATAR_ROW_CLASSNAME}>
        {/* LosiAnimated engine via LosiAvatar — do not strip */}
        <div className={CREW_FEED_AVATAR_DECORATIVE_SHELL_CLASSNAME} aria-hidden>
          <LosiAvatar
            shape={resolved.shape}
            color="blanc"
            size={40}
            fitBody
            state={CREW_MEMBER_CHROME_STATUS_IDLE}
          />
        </div>
        <div className={CREW_MEMBER_CHROME_COPY_COLUMN_CLASSNAME}>
          <p className={CREW_MEMBER_DISPLAY_NAME_CLASSNAME}>
            {resolved.displayLabel}
          </p>
          <p className={CREW_FEED_STATUS_MUTED_CLASSNAME}>
            {resolved.mateLabel}
            {resolved.usesFaceFallback && CREW_MEMBER_CHROME_FACE_FALLBACK_LABEL
              ? ` · ${CREW_MEMBER_CHROME_FACE_FALLBACK_LABEL}`
              : ''}
            {CREW_MEMBER_BIND_STATUS_LABEL
              ? ` · ${CREW_MEMBER_BIND_STATUS_LABEL}`
              : ''}
          </p>
        </div>
      </div>

      {CREW_MEMBER_CHROME_HONESTY ? (
        <p className={CREW_MEET_YOUR_CREW_HONESTY_NOTE_CLASSNAME} role="note">
          {CREW_MEMBER_CHROME_HONESTY}
        </p>
      ) : null}
    </section>
  )
}

export function CrewMemberChrome({
  className,
  variant = 'panel',
  model,
  roster,
  activeMateId,
  activityByMateId
}: CrewMemberChromeProps) {
  if (variant === 'header') {
    if (!roster) return null
    return (
      <CrewMemberChromeHeader
        activeMateId={activeMateId}
        activityByMateId={activityByMateId}
        className={className}
        roster={roster}
      />
    )
  }

  return <CrewMemberChromePanel className={className} model={model} />
}
